import { utcToUserTimezone, getBrowserTimezone } from '@/utils/date/formatters';
import type { AppTheme } from '@/theme/theme';

interface EventLog {
  id: bigint | number;
  log_id: string;
  serial_number: string;
  record_index: number;
  timestamp: string;
  event_type: number;
  event_type_text: string;
  description: string | null;
  is_error: boolean;
  is_critical: boolean;
  event_data1?: bigint | number | null;
  event_data1_text?: string | null;
  event_data2?: number | null;
  event_data2_text?: string | null;
  event_data3?: number | null;
  event_data3_text?: string | null;
  log_type?: number;
  event_data?: number | null;
  event_data_label?: string | null;
}

interface TimelineInterval {
  category: TimelineCategory;
  start: Date;
  end: Date;
  durationMs: number;
}

interface PointEvent {
  category: string;
  time: Date;
  label: string;
  severity: 'normal' | 'error' | 'critical';
  eventData: string;
  description: string;
}

export type TimelineCategory = 'operating' | 'ble' | 'charging';

export const CATEGORY_I18N_KEY: Record<TimelineCategory, string> = {
  operating: 'datalog:timelineOperating',
  ble: 'datalog:timelineBle',
  charging: 'datalog:timelineCharging',
};

export function getCategoryColor(category: TimelineCategory, theme: AppTheme): string {
  switch (category) {
    case 'operating':
      return theme.colors.status.stable.fill.surface as string;
    case 'ble':
      return theme.colors.status.calib.fill.surface as string;
    case 'charging':
      return theme.colors.status.caution.fill.surface as string;
  }
}

const POINT_EVENT_TYPES = new Set([5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]);

function toLocalDate(utcTimestamp: string, tz?: string): Date {
  return utcToUserTimezone(utcTimestamp, tz);
}

function buildPairIntervals(
  sortedLogs: EventLog[],
  category: TimelineCategory,
  startTypes: number[],
  endTypes: number[],
  tz?: string
): TimelineInterval[] {
  const intervals: TimelineInterval[] = [];
  const relevant = sortedLogs.filter(
    l => startTypes.includes(l.event_type) || endTypes.includes(l.event_type)
  );

  let currentStart: Date | null = null;

  for (const log of relevant) {
    const t = toLocalDate(log.timestamp, tz);

    if (startTypes.includes(log.event_type)) {
      if (currentStart) {
        intervals.push({
          category,
          start: currentStart,
          end: t,
          durationMs: t.getTime() - currentStart.getTime(),
        });
      }
      currentStart = t;
    } else if (endTypes.includes(log.event_type) && currentStart) {
      intervals.push({
        category,
        start: currentStart,
        end: t,
        durationMs: t.getTime() - currentStart.getTime(),
      });
      currentStart = null;
    }
  }

  // If still open, extend to last log timestamp in the dataset
  if (currentStart && sortedLogs.length > 0) {
    const lastTs = toLocalDate(sortedLogs[sortedLogs.length - 1].timestamp, tz);
    if (lastTs > currentStart) {
      intervals.push({
        category,
        start: currentStart,
        end: lastTs,
        durationMs: lastTs.getTime() - currentStart.getTime(),
      });
    }
  }

  return intervals.filter(i => i.durationMs > 0);
}

/**
 * Charging inferred from PowerOff → FullCharged/RemovedCradleCharging.
 */
function buildChargingIntervals(sortedLogs: EventLog[], tz?: string): TimelineInterval[] {
  const intervals: TimelineInterval[] = [];
  const relevant = sortedLogs.filter(l => [1, 2, 16, 17].includes(l.event_type));

  for (let i = 0; i < relevant.length; i++) {
    if (relevant[i].event_type !== 2) continue;

    const offTime = toLocalDate(relevant[i].timestamp, tz);
    let chargingEnd: Date | null = null;
    let hasChargingEvent = false;

    for (let j = i + 1; j < relevant.length; j++) {
      const next = relevant[j];
      const nextTime = toLocalDate(next.timestamp, tz);

      if (next.event_type === 16 || next.event_type === 17) {
        hasChargingEvent = true;
        chargingEnd = nextTime;
      }
      if (next.event_type === 1) {
        if (!chargingEnd) chargingEnd = nextTime;
        break;
      }
    }

    if (hasChargingEvent && chargingEnd && chargingEnd > offTime) {
      intervals.push({
        category: 'charging',
        start: offTime,
        end: chargingEnd,
        durationMs: chargingEnd.getTime() - offTime.getTime(),
      });
    }
  }

  return intervals;
}

export interface FullTimeline {
  intervals: TimelineInterval[];
  pointEvents: PointEvent[];
  rangeStart: Date;
  rangeEnd: Date;
}

/**
 * Build the full timeline from all event logs across the entire date range.
 * No day slicing — the chart will handle zoom/scroll.
 */
export function buildFullTimeline(
  logs: EventLog[],
  fromDate: string,
  toDate: string
): FullTimeline {
  const tz = getBrowserTimezone();

  if (logs.length === 0) {
    return {
      intervals: [],
      pointEvents: [],
      rangeStart: new Date(fromDate),
      rangeEnd: new Date(toDate + 'T23:59:59'),
    };
  }

  const sorted = [...logs].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const intervals: TimelineInterval[] = [
    ...buildPairIntervals(sorted, 'operating', [1], [2], tz),
    ...buildPairIntervals(sorted, 'ble', [3], [4], tz),
    ...buildChargingIntervals(sorted, tz),
  ];

  const pointEvents: PointEvent[] = sorted
    .filter(l => POINT_EVENT_TYPES.has(l.event_type))
    .map(l => {
      let eventData = '';
      if (l.event_data1_text) eventData += l.event_data1_text;
      if (l.event_data2_text) eventData += (eventData ? ', ' : '') + l.event_data2_text;
      if (l.event_data3_text) eventData += (eventData ? ', ' : '') + l.event_data3_text;
      if (l.event_data_label) eventData = l.event_data_label;

      return {
        category: l.event_type_text,
        time: toLocalDate(l.timestamp, tz),
        label: l.event_type_text,
        severity: l.is_critical
          ? ('critical' as const)
          : l.is_error
            ? ('error' as const)
            : ('normal' as const),
        eventData: eventData || '-',
        description: l.description || '-',
      };
    });

  const rangeStart = new Date(fromDate);
  const rangeEnd = new Date(toDate + 'T23:59:59');

  return { intervals, pointEvents, rangeStart, rangeEnd };
}

export function formatDuration(ms: number): string {
  if (ms <= 0) return '-';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return '< 1m';
}

export function calcTotalByCategory(
  intervals: TimelineInterval[]
): Record<TimelineCategory, number> {
  const totals: Record<TimelineCategory, number> = { operating: 0, ble: 0, charging: 0 };
  for (const i of intervals) {
    totals[i.category] += i.durationMs;
  }
  return totals;
}
